'use client'

import * as React from 'react'
import Script from 'next/script'
import { useConsent } from '@/lib/consent'
import { PIXEL_IDS } from '@/lib/consent/pixel-config'

// Single audit point for every third-party pixel on the site. Each
// block is gated on its consent category via the `effective` view
// (so the live-chat widget can fire pre-decision while ad pixels stay
// off). Cookies the user revokes mid-session: we stop firing the
// pixel's methods but do not try to tear DOM nodes down, most
// vendors don't expose a clean unload path.

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void
      queue?: unknown[]
      loaded?: boolean
      version?: string
      push?: unknown
    }
    _fbq?: unknown
    clarity?: (...args: unknown[]) => void
    // ClickConnector ships its widget constructor on this key. The 4s
    // post-load delay before calling `.mount({})` is intentional, see
    // their docs.
    'click-connector-widget'?: new () => { mount: (cfg: Record<string, unknown>) => void }
  }
}

// Fires on first user interaction (scroll/move/touch/key) OR after delayMs —
// whichever is earlier. A real visitor who interacts gets every gated script
// on the same tick (fine: interaction is already a strong "page is settled"
// signal). A crawl with no interaction (Lighthouse/PSI) only ever hits the
// delayMs branch, so the call sites below use staggered delays to spread
// that branch's script-injection cost across time instead of bursting six
// scripts into the same event-loop tick — that simultaneous burst, not the
// scripts existing, is what was producing multi-second TBT.
function useDelayedReady(delayMs = 5000): boolean {
  const [ready, setReady] = React.useState(false)
  React.useEffect(() => {
    let fired = false
    const fire = () => {
      if (fired) return
      fired = true
      setReady(true)
    }
    const tid = setTimeout(fire, delayMs)
    const opts = { once: true, passive: true } as const
    window.addEventListener('mousemove', fire, opts)
    window.addEventListener('scroll', fire, opts)
    window.addEventListener('touchstart', fire, opts)
    window.addEventListener('keydown', fire, opts)
    return () => {
      clearTimeout(tid)
      window.removeEventListener('mousemove', fire)
      window.removeEventListener('scroll', fire)
      window.removeEventListener('touchstart', fire)
      window.removeEventListener('keydown', fire)
    }
  }, [delayMs])
  return ready
}

export function PixelLoader() {
  const { effective } = useConsent()
  const analytics = effective.analytics
  const marketing = effective.marketing
  const functional = effective.functional
  // Staggered by 400ms per script so a no-interaction crawl (Lighthouse/PSI)
  // never injects and evaluates all six B2B/chat scripts in the same tick.
  // A real visitor who interacts before any of these fire is unaffected —
  // interaction short-circuits useDelayedReady immediately, same as before.
  const b2bReadyRb2b = useDelayedReady(5000)
  const b2bReadyKoala = useDelayedReady(5400)
  const b2bReadyApollo = useDelayedReady(5800)
  const b2bReadyLeadsy = useDelayedReady(6200)
  const b2bReadyVector = useDelayedReady(6600)
  const b2bReadyChat = useDelayedReady(7000)

  return (
    <>
      {/* GA4, gated on analytics */}
      {analytics ? (
        <>
          <Script
            id="px-ga4-loader"
            src={`https://www.googletagmanager.com/gtag/js?id=${PIXEL_IDS.ga4}`}
            strategy="afterInteractive"
          />
          <Script id="px-ga4-bootstrap" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){window.dataLayer.push(arguments);}
              window.gtag = gtag;
              gtag('js', new Date());
              gtag('config', '${PIXEL_IDS.ga4}');
            `}
          </Script>
        </>
      ) : null}

      {/* Meta Pixel, gated on marketing */}
      {marketing ? (
        <>
          <Script id="px-meta" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
              n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
              document,'script','https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${PIXEL_IDS.meta}');
              fbq('track', 'PageView');
            `}
          </Script>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              alt=""
              src={`https://www.facebook.com/tr?id=${PIXEL_IDS.meta}&ev=PageView&noscript=1`}
            />
          </noscript>
        </>
      ) : null}

      {/* Ahrefs Web Analytics (traffic), gated on analytics */}
      {analytics ? (
        <Script
          id="px-ahrefs"
          async
          src="https://analytics.ahrefs.com/analytics.js"
          data-key={PIXEL_IDS.ahrefs}
          strategy="afterInteractive"
        />
      ) : null}

      {/* Microsoft Clarity, gated on analytics */}
      {analytics ? (
        <Script id="px-clarity" strategy="afterInteractive">
          {`
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${PIXEL_IDS.clarity}");
          `}
        </Script>
      ) : null}

      {/*
        Five B2B identity-resolution tools (RB2B / Koala / Apollo /
        Leadsy / Vector). Each fires on first user interaction, or after
        its own delay (5.0-6.6s, staggered 400ms apart) if there's none.
        A real visitor who interacts gets them all immediately; a
        no-interaction crawl (Lighthouse/PSI) only ever hits the delay
        branch, and the stagger keeps that branch from injecting all
        five scripts into the same event-loop tick.
      */}

      {/* RB2B, gated on marketing + b2bReadyRb2b */}
      {marketing && b2bReadyRb2b ? (
        <Script id="px-rb2b" strategy="afterInteractive">
          {`
            !function(){var reb2b=window.reb2b=window.reb2b||[];if(reb2b.invoked)return;reb2b.invoked=true;reb2b.methods=["identify","collect"];reb2b.factory=function(method){return function(){var args=Array.prototype.slice.call(arguments);args.unshift(method);reb2b.push(args);return reb2b}};for(var i=0;i<reb2b.methods.length;i++){var key=reb2b.methods[i];reb2b[key]=reb2b.factory(key)}reb2b.load=function(key){var script=document.createElement("script");script.type="text/javascript";script.async=true;script.src="https://s3-us-west-2.amazonaws.com/b2bjsstore/b/"+key+"/reb2b.js.gz";var first=document.getElementsByTagName("script")[0];first.parentNode.insertBefore(script,first)};reb2b.SNIPPET_VERSION="1.0.1";reb2b.load("${PIXEL_IDS.rb2b}")}();
          `}
        </Script>
      ) : null}

      {/* Koala, gated on marketing + b2bReadyKoala */}
      {marketing && b2bReadyKoala ? (
        <Script id="px-koala" strategy="afterInteractive">
          {`
            !function(t){if(window.ko)return;window.ko=[],["identify","track","removeListeners","open","on","off","qualify","ready"].forEach(function(t){ko[t]=function(){var n=[].slice.call(arguments);return n.unshift(t),ko.push(n),ko}});var n=document.createElement("script");n.async=!0,n.setAttribute("src","https://cdn.getkoala.com/v1/${PIXEL_IDS.koala}/sdk.js"),(document.body||document.head).appendChild(n)}();
          `}
        </Script>
      ) : null}

      {/* Apollo.io tracker, gated on marketing + b2bReadyApollo */}
      {marketing && b2bReadyApollo ? (
        <Script id="px-apollo" strategy="afterInteractive">
          {`
            function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,o.onload=function(){window.trackingFunctions.onLoad({appId:"${PIXEL_IDS.apollo}"})},document.head.appendChild(o)}initApollo();
          `}
        </Script>
      ) : null}

      {/* Leadsy.ai, gated on marketing + b2bReadyLeadsy */}
      {marketing && b2bReadyLeadsy ? (
        <Script
          id="vtag-ai-js"
          async
          src="https://r2.leadsy.ai/tag.js"
          data-pid={PIXEL_IDS.leadsy}
          data-version="062024"
          strategy="afterInteractive"
        />
      ) : null}

      {/* Vector.co, gated on marketing + b2bReadyVector */}
      {marketing && b2bReadyVector ? (
        <Script id="px-vector" strategy="afterInteractive">
          {`
            !function(e,r){try{if(e.vector)return void console.log("Vector snippet included more than once.");var t={};t.q=t.q||[];for(var o=["load","identify","on"],n=function(e){return function(){var r=Array.prototype.slice.call(arguments);t.q.push([e,r])}},c=0;c<o.length;c++){var a=o[c];t[a]=n(a)}if(e.vector=t,!t.loaded){var i=r.createElement("script");i.type="text/javascript",i.async=!0,i.src="https://cdn.vector.co/pixel.js";var l=r.getElementsByTagName("script")[0];l.parentNode.insertBefore(i,l),t.loaded=!0}}catch(e){console.error("Error loading Vector:",e)}}(window,document);vector.load("${PIXEL_IDS.vector}");
          `}
        </Script>
      ) : null}

      {/*
        Click Connector live chat, gated on functional + its own staggered
        delay (7s, last in the sequence above). ClickConnector does not
        expose a clean teardown path; if the user opts out mid-session the
        widget stays until the next reload. We accept that limitation
        rather than try to rip the iframe out. A 7s-or-first-interaction
        delay before the chat bubble appears is imperceptible to a real
        visitor still reading the page.
      */}
      {functional && b2bReadyChat ? (
        <Script
          id="cc-widget-script"
          src="https://widget.clickconnector.app/widget.js"
          data-widget-id={PIXEL_IDS.clickConnector}
          strategy="afterInteractive"
          defer
          onLoad={() => {
            // Vendor's 4s delay before mounting, kept verbatim.
            window.setTimeout(() => {
              try {
                const Ctor = window['click-connector-widget']
                if (Ctor) new Ctor().mount({})
              } catch {
                /* swallow, never let chat-widget bootstrap crash the page */
              }
            }, 4000)
          }}
        />
      ) : null}
    </>
  )
}
